import { Button } from "@2u-landing-pages/components/Button";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { useParseMeta } from "@2u-landing-pages/providers";
import { renderEmbeddedEntry, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body, ButtonGroup, Container, Heading } from "@red-digital/bricks";
import cn from "classnames";
import React from "react";
import { BannerProps, CustomCSSProperties } from "../../Banner.types";

// @ts-ignore
import bannerStyles from "../../Banner.module.scss";

const CTA: React.FC<Omit<BannerProps, "variation">> = ({
  main_content,
  button,
  background_color,
  inspector,
  className,
  location,
  phoneNumber,
  sticky,
  stripFocusContext,
  style: defaultStyle,
}) => {
  const parseMetadata = useParseMeta();

  if (!main_content && !button) return null;

  const style: CustomCSSProperties = {
    ...defaultStyle,
  };

  const heading = main_content?.fields?.heading;
  const headingColor = main_content?.fields?.heading_color;
  const subhead = main_content?.fields?.subhead;
  const subheadColor = main_content?.fields?.subhead_color;
  const body = main_content?.fields?.body;
  const bodyColor = main_content?.fields?.body_color;

  if (heading && headingColor) style["--heading-color"] = `var(${headingColor})`;
  if (subhead && subheadColor) style["--subhead-color"] = `var(${subheadColor})`;
  if (body && bodyColor) style["--body-color"] = `var(${bodyColor})`;

  const renderRichText = (
    field: typeof heading,
    fieldId: string,
    Component: typeof Heading | typeof Body,
    variant: string,
    styleClass: string,
  ) => {
    if (!field || typeof field !== "object") return null;
    return documentToReactComponents(field, {
      renderText: (text) => renderText(parseMetadata?.(text) || text, phoneNumber),
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => (
          <Component
            {...inspector({
              entryId: main_content!.sys.id,
              fieldId,
            })}
            variant={variant}
            className={styleClass}
          >
            {children}
          </Component>
        ),
        [INLINES.EMBEDDED_ENTRY]: (node) =>
          renderEmbeddedEntry(node, "CTA Banner", field, phoneNumber, parseMetadata),
      },
    });
  };

  return (
    <TemplateWrapper
      data-template="banner"
      {...(stripFocusContext ? { "data-strip-focus-context": stripFocusContext } : {})}
      backgroundColor={background_color}
      className={cn(className, {
        [bannerStyles.sticky]: sticky,
      })}
      style={style}
    >
      <Container flex direction="row" justifyContent="center" alignItems="center" className={bannerStyles.banner}>
        {renderRichText(heading, "heading", Heading, "heading-lg", bannerStyles.heading)}
        {renderRichText(subhead, "subhead", Heading, "subhead-sm", bannerStyles.subhead)}
        {renderRichText(body, "body", Body, "body-md", bannerStyles.body)}
        {Boolean(button) && (
          <ButtonGroup>
            <Button
              key={button?.sys.id}
              location={location}
              {...button?.fields}
              phoneNumber={phoneNumber}
            >
              {button?.fields.label}
            </Button>
          </ButtonGroup>
        )}
      </Container>
    </TemplateWrapper>
  );
};

export default CTA;
